import React, { createContext, useContext, useEffect, useRef, useCallback, useState } from 'react';
import PlatformBridge from '../utils/PlatformBridge';

const RECONNECT_BASE_DELAY_MS = 1000;
const MAX_RECONNECT_DELAY_MS = 30000;
const HEARTBEAT_INTERVAL_MS = 15000;
const HEARTBEAT_TIMEOUT_MS = 45000;

function resolveWsBaseUrl() {
  const explicitWs = process.env.REACT_APP_WS_BASE_URL;
  if (explicitWs && explicitWs.trim()) {
    return explicitWs.trim().replace(/^http/, 'ws').replace(/\/$/, '');
  }
  if (process.env.NODE_ENV === 'production') {
    return 'wss://ws.axium.com.uy';
  }
  // En local los WebSockets los sirve axium-ws (puerto 8090).
  return 'ws://localhost:8090';
}

function getWebSocketUrl() {
  return `${resolveWsBaseUrl()}/ws`;
}

const WebSocketContext = createContext(null);

function normalizeEventName(eventName) {
  if (typeof eventName !== 'string') return null;
  const value = eventName.trim().toLowerCase();
  return value || null;
}

function parseJsonIfString(value) {
  if (typeof value !== 'string') return value;
  try {
    return JSON.parse(value);
  } catch (_) {
    return value;
  }
}

function normalizeWsPayload(raw) {
  const parsed = parseJsonIfString(raw);
  if (!parsed || typeof parsed !== 'object') return null;

  const directEvent = normalizeEventName(parsed.event);
  if (directEvent) {
    return { event: directEvent, data: parseJsonIfString(parsed.data) };
  }

  const candidates = [parsed.message, parsed.payload, parsed.data];
  for (const candidate of candidates) {
    const nested = parseJsonIfString(candidate);
    if (!nested || typeof nested !== 'object') continue;
    const nestedEvent = normalizeEventName(nested.event);
    if (nestedEvent) {
      return { event: nestedEvent, data: parseJsonIfString(nested.data) };
    }
  }

  return null;
}

export function WebSocketProvider({ children }) {
  const wsRef = useRef(null);
  const connectingRef = useRef(false);
  const reconnectTimeoutRef = useRef(null);
  const reconnectDelayRef = useRef(RECONNECT_BASE_DELAY_MS);
  const heartbeatIntervalRef = useRef(null);
  const heartbeatWatchdogRef = useRef(null);
  const connectedTokenRef = useRef(null);
  const [connected, setConnected] = useState(false);
  const [healthy, setHealthy] = useState(false);
  /** Map: eventType -> Set<callback> */
  const subscribersRef = useRef(new Map());

  const subscribe = useCallback((eventType, callback) => {
    const normalizedEventType = normalizeEventName(eventType);
    if (!normalizedEventType) {
      return () => {};
    }
    if (!subscribersRef.current.has(normalizedEventType)) {
      subscribersRef.current.set(normalizedEventType, new Set());
    }
    subscribersRef.current.get(normalizedEventType).add(callback);
    return () => {
      subscribersRef.current.get(normalizedEventType)?.delete(callback);
    };
  }, []);

  useEffect(() => {
    const stopHeartbeat = () => {
      if (heartbeatIntervalRef.current) {
        clearInterval(heartbeatIntervalRef.current);
        heartbeatIntervalRef.current = null;
      }
      if (heartbeatWatchdogRef.current) {
        clearTimeout(heartbeatWatchdogRef.current);
        heartbeatWatchdogRef.current = null;
      }
    };

    const scheduleWatchdog = () => {
      if (heartbeatWatchdogRef.current) {
        clearTimeout(heartbeatWatchdogRef.current);
      }
      heartbeatWatchdogRef.current = setTimeout(() => {
        setHealthy(false);
        if (wsRef.current) {
          wsRef.current.close();
        }
      }, HEARTBEAT_TIMEOUT_MS);
    };

    const startHeartbeat = () => {
      stopHeartbeat();
      scheduleWatchdog();
      heartbeatIntervalRef.current = setInterval(() => {
        const ws = wsRef.current;
        if (!ws || ws.readyState !== WebSocket.OPEN) return;
        try {
          ws.send(JSON.stringify({ event: 'ping' }));
        } catch (_) {
          setHealthy(false);
          ws.close();
          return;
        }
        scheduleWatchdog();
      }, HEARTBEAT_INTERVAL_MS);
    };

    const scheduleReconnect = () => {
      const jitter = Math.floor(Math.random() * 500);
      const delay = reconnectDelayRef.current + jitter;
      reconnectTimeoutRef.current = setTimeout(() => {
        connect();
        reconnectDelayRef.current = Math.min(
          Math.round(reconnectDelayRef.current * 2),
          MAX_RECONNECT_DELAY_MS
        );
      }, delay);
    };

    const connect = async () => {
      // connect() es async y arranca esperando el token (en Electron es una vuelta real de
      // IPC): dos triggers casi simultáneos (ej. el timeout de reconexión y que la pestaña
      // vuelva a estar visible) pueden quedar los dos pausados ahí a la vez. connectingRef
      // cierra esa ventana -- el segundo en llegar corta antes de tocar wsRef, en vez de
      // terminar creando un socket nuevo que pisa la referencia al que ya está conectando.
      if (connectingRef.current) return;
      connectingRef.current = true;
      try {
        const token = await PlatformBridge.getToken('accessToken');
        const empresaId = localStorage.getItem('empresaId');
        if (!token || !empresaId || empresaId === 'undefined' || empresaId === 'null') {
          return;
        }
        // Ya hay un socket para este mismo token abierto O conectando: no crear otro. Sin el
        // caso CONNECTING, dos llamadas que arrancan con el primer guard todavía sin setear
        // (por ejemplo, una que arranca justo después de que la otra ya soltó connectingRef
        // pero antes de que su socket termine de abrir) pasan las dos de largo, y la segunda
        // pisa wsRef con un socket nuevo dejando el primero conectando "a la deriva": si más
        // tarde ese socket huérfano dispara onclose, pone wsRef.current en null igual, aunque
        // en ese momento apunte al socket bueno.
        if (
          wsRef.current &&
          connectedTokenRef.current === token &&
          (wsRef.current.readyState === WebSocket.OPEN || wsRef.current.readyState === WebSocket.CONNECTING)
        ) {
          return;
        }
        if (wsRef.current && connectedTokenRef.current !== token) {
          wsRef.current.close();
          wsRef.current = null;
          setConnected(false);
          setHealthy(false);
        }

        const url = `${getWebSocketUrl()}?token=${encodeURIComponent(token)}`;
        const ws = new WebSocket(url);
        wsRef.current = ws;
        connectedTokenRef.current = token;

        ws.onopen = () => {
          setConnected(true);
          setHealthy(true);
          reconnectDelayRef.current = RECONNECT_BASE_DELAY_MS;
          startHeartbeat();
        };

        ws.onmessage = (event) => {
          try {
            const parsed = JSON.parse(event.data);
            const msg = normalizeWsPayload(parsed);
            const eventType = msg?.event;
            if (!eventType) return;

            if (eventType === 'pong') {
              setHealthy(true);
              scheduleWatchdog();
              return;
            }

            const data = msg?.data;
            const callbacks = subscribersRef.current.get(eventType);
            if (callbacks) {
              callbacks.forEach((cb) => {
                try {
                  if (typeof cb === 'function') cb(data);
                } catch (e) {
                  console.warn('WebSocket subscriber error:', e);
                }
              });
            }
          } catch (e) {
            console.warn('WebSocket mensaje no JSON:', e);
          }
        };

        ws.onclose = (event) => {
          stopHeartbeat();
          setConnected(false);
          setHealthy(false);
          // Solo soltar la referencia si este socket sigue siendo el vigente -- un socket
          // huérfano (no debería poder crearse ya con los guards de arriba, pero por las
          // dudas) no debe poder pisarle la referencia a uno más nuevo que sí está andando.
          if (wsRef.current === ws) {
            wsRef.current = null;
          }
          const reason = (event.reason || '').toLowerCase();
          const tokenInvalid = reason.includes('token_invalid') || reason.includes('token_required');
          const rejected =
            tokenInvalid ||
            event.code === 4403 ||
            event.code === 4400 ||
            event.code === 1003 ||
            (event.code >= 4400 && event.code <= 4499) ||
            event.code === 1008;
          if (rejected) return;
          scheduleReconnect();
        };

        ws.onerror = () => {
          setHealthy(false);
        };
      } finally {
        connectingRef.current = false;
      }
    };

    const disconnect = () => {
      stopHeartbeat();
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
      connectedTokenRef.current = null;
      setConnected(false);
      setHealthy(false);
    };

    const onTokenRefreshed = () => {
      disconnect();
      connect();
    };
    window.addEventListener('tokenRefreshed', onTokenRefreshed);

    const onVisibilityChange = () => {
      if (document.visibilityState !== 'visible') return;
      if (wsRef.current?.readyState === WebSocket.OPEN) return;
      reconnectDelayRef.current = RECONNECT_BASE_DELAY_MS;
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      connect();
    };
    document.addEventListener('visibilitychange', onVisibilityChange);

    let mounted = true;
    (async () => {
      const token = await PlatformBridge.getToken('accessToken');
      const empresaId = localStorage.getItem('empresaId');
      if (mounted && token && empresaId && empresaId !== 'undefined' && empresaId !== 'null') {
        connect();
      }
    })();

    return () => {
      mounted = false;
      window.removeEventListener('tokenRefreshed', onTokenRefreshed);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      disconnect();
    };
  }, []);

  const value = { connected, healthy, subscribe };

  return (
    <WebSocketContext.Provider value={value}>
      {children}
    </WebSocketContext.Provider>
  );
}

export function useWebSocket() {
  const ctx = useContext(WebSocketContext);
  if (!ctx) {
    throw new Error('useWebSocket must be used within WebSocketProvider');
  }
  return ctx;
}
